import React, { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import useGetOrderDetail from "../../apis/useGetOrderDetail/useGetOrderDetail";
import { useLocalStorage } from "../../common/hooks/useLocalStorage";
import ForceUpdateModal from "../../components/ForceUpdateModal/ForceUpdateModal";
import { useCartActions } from "../../contexts/CartServiceContext/CartServiceContext";
import { useConfigContext } from "../../contexts/ConfigContext/ConfigContext";
import { usePaymentActions, usePaymentContext } from "../../contexts/PaymentContext/PaymentContext";
import { PATH } from "../../router/path";
import {
	addDatadogAction,
	buildLocationContext,
	startNewDatadogSession,
} from "../../services/datadog";
import { useForceUpdate } from "../../services/useForceUpdate";
import OrderFail from "./OrderFail";
import OrderLayoutDefault from "./OrderLayoutDefault";
import OrderLoading from "./OrderLoading";

const OrderContainer: React.FC = () => {
	const navigate = useNavigate();
	const { CLEAR_PAYMENT_DATA } = usePaymentActions();
	const { CLEAN_CART } = useCartActions();
	const { state: location } = useConfigContext((s) => s.location);
	const { state: stall } = useConfigContext((s) => s.config.stall);
	const { state: paymentReference } = usePaymentContext((s) => s.paymentReference);
	const { setValue: setProductListScroll } = useLocalStorage("PRODUCT_LIST_SCROLL", {
		scrollTop: "0",
		activeCategoryId: null,
	});
	const { showModal, checkAndProceed, handleReload } = useForceUpdate();
	// Data Fetching: The hook now finds the reference by itself.
	const { orderDetail, isLoading, error } = useGetOrderDetail({
		autoFetch: true,
	});

	const orderLoadedRef = useRef(false);
	const forceUpdateShownRef = useRef(false);

	useEffect(() => {
		addDatadogAction("order_confirmation_page_mount", {
			...buildLocationContext(location, stall),
			orderId: paymentReference ?? null,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Kiosk leaves the order open if the customer walks away. After 5 min
	// auto-route to Landing so the next walk-up doesn't see prior order info.
	useEffect(() => {
		const timeoutId = window.setTimeout(
			() => {
				addDatadogAction("order_confirmation_auto_startover", {
					...buildLocationContext(location, stall),
					orderId: paymentReference ?? null,
				});
				startNewDatadogSession();
				setProductListScroll({ scrollTop: "0", activeCategoryId: null });
				navigate(PATH.LANDING);
			},
			5 * 60 * 1000
		);

		return () => window.clearTimeout(timeoutId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (isLoading) return;
		if (orderLoadedRef.current) return;

		// Fire once when loading transitions to false, regardless of success/fail.
		const status: "success" | "fail" = error || !orderDetail ? "fail" : "success";
		const itemCount = orderDetail?.order_items?.item_info?.length ?? 0;

		addDatadogAction("order_confirmation_order_loaded", {
			...buildLocationContext(location, stall),
			orderId: paymentReference ?? null,
			status,
			itemCount,
		});
		orderLoadedRef.current = true;
	}, [isLoading, error, orderDetail, location, stall, paymentReference]);

	useEffect(() => {
		if (!showModal) return;
		if (forceUpdateShownRef.current) return;

		addDatadogAction("order_confirmation_force_update_shown", {
			...buildLocationContext(location, stall),
			orderId: paymentReference ?? null,
		});
		forceUpdateShownRef.current = true;
	}, [showModal, location, stall, paymentReference]);

	const handleForceUpdateReload = useCallback(() => {
		addDatadogAction("order_confirmation_force_update_reload", {
			...buildLocationContext(location, stall),
			orderId: paymentReference ?? null,
		});
		handleReload();
	}, [handleReload, location, stall, paymentReference]);

	// Action Handler
	const handleStartOver = useCallback(() => {
		addDatadogAction("order_confirmation_start_over", {
			...buildLocationContext(location, stall),
			orderId: paymentReference ?? null,
		});
		startNewDatadogSession();
		setProductListScroll({
			scrollTop: "0",
			activeCategoryId: null,
		});
		navigate(PATH.LANDING);
	}, [navigate, setProductListScroll, location, stall, paymentReference]);

	const handleStartOverWithCheck = useCallback(() => {
		checkAndProceed(handleStartOver);
	}, [checkAndProceed, handleStartOver]);

	if (isLoading) {
		return <OrderLoading />;
	}

	if (error || !orderDetail) {
		return (
			<>
				{showModal && <ForceUpdateModal onReload={handleForceUpdateReload} />}
				<OrderFail onStartOver={handleStartOverWithCheck} />
			</>
		);
	}

	return (
		<>
			{showModal && <ForceUpdateModal onReload={handleForceUpdateReload} />}
			<OrderLayoutDefault orderDetail={orderDetail} onStartOver={handleStartOverWithCheck} />
		</>
	);
};

export default OrderContainer;
